/**
 * What to load for a view, and when a load can be skipped (DESIGN.md §10.5; STRATEGY.md 2026-10-02).
 *
 * The viewer used to load exactly the visible window, so every pan waited for a manifest request
 * and its byte ranges before the trace moved — the grid panned, the trace stayed put. Now a window
 * one span wider on each side is loaded at the same resolution, the viewer draws whatever part of it
 * is in view, and a pan or a jump that stays inside it needs no request at all.
 */

export interface Loaded {
  /** First and one-past-last sample of what was requested. */
  start: number;
  end: number;
  /** The view span it was loaded for: a different zoom needs a different pyramid level. */
  viewSpan: number;
}

/** The window to load for a view: the view plus one span either side, clamped to the recording. */
export function bufferFor(start: number, span: number, total: number): { start: number; span: number } {
  const s = Math.max(0, Math.round(start - span));
  const e = Math.min(total, Math.round(start + 2 * span));
  return { start: s, span: Math.max(1, e - s) };
}

/** Whether `loaded` already covers the view at the same zoom (within 1%), so no request is needed. */
export function covers(loaded: Loaded | null, start: number, span: number): boolean {
  if (!loaded) return false;
  if (Math.abs(loaded.viewSpan - span) > span * 0.01) return false;
  return start >= loaded.start && start + span <= loaded.end;
}

/** Canvas pixels to request for a buffer so it comes back at the resolution the view is drawn at. */
export function pixelsFor(viewPx: number, bufferSpan: number, viewSpan: number): number {
  return Math.max(1, Math.round((viewPx * bufferSpan) / Math.max(1, viewSpan)));
}
